import { InfoCircleOutlined } from '@ant-design/icons';
import { Heading, RadioInput, Switch, Tooltip } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { QuestionCircleIcon } from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useIceBreakerCRUDActions } from '@mm/state/actions/IceBreakerCRUDActions';
import { selectFacebookIceBreaker } from '@mm/state/selectors/iceBreakerSelectors';
import { welcomePageSelector } from '@mm/state/selectors/onboardingSelectors';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import React, { useEffect } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';

import { IceBreaker } from 'src/editor/features/facebookPageWelcomer/IceBreaker/IceBreaker';
import {
  getMMdndEvents,
  MMdndContext,
} from '../../../../../common/helpers/MMdndContext';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { IceBreakerPreview } from '../../../../features/facebookPageWelcomer/IceBreaker/IceBreakerPreview/IceBreakerPreview';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './FBWelcomerSlideoutEditor.module.css';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';

export const FBWelcomerSlideoutEditor = () => {
  const iceBreakerCRUDActions = useIceBreakerCRUDActions();
  const botCRUDActions = useChatbotCRUDActions();
  const welcomePage = useSelector(welcomePageSelector);
  const chatbot = useSelector(activeChatbotSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const iceBreaker = useSelector(selectFacebookIceBreaker);
  const dndEvents = getMMdndEvents();
  const { setRef } = useSlideoutOnClose();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  useEffect(() => {
    async function fetchItems() {
      await iceBreakerCRUDActions.retrieveAll(chatbotId, 'facebook');
    }
    fetchItems();
  }, [chatbotId, iceBreakerCRUDActions]);

  if (!iceBreaker || !welcomePage) {
    return null;
  }

  const activateIceBreaker = (active: boolean) => {
    iceBreakerCRUDActions.update(chatbotId, { id: iceBreaker.id, active });
  };
  const onStatusChanged = () => {
    botCRUDActions.update({
      id: chatbotId,
      fbWelcomerDisabled: !chatbot.fbWelcomerDisabled,
    });
  };

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <SlideoutMenu.Header className={styles.header} closeIcon="close">
        <Heading size={3}>Facebook DM Autoresponder</Heading>
        <Switch
          checked={!chatbot.fbWelcomerDisabled}
          disabled={false}
          onChange={onStatusChanged}
          label={!chatbot.fbWelcomerDisabled ? 'active' : 'inactive'}
          css={{
            container: styles.switch,
            thumb: !chatbot.fbWelcomerDisabled
              ? styles.thumbActive
              : styles.thumbInactive,
            label: !chatbot.fbWelcomerDisabled
              ? styles.switchLabelActive
              : styles.switchLabelInactive,
          }}
        />
      </SlideoutMenu.Header>

      <FreeTrialWarning />

      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.configArea}>
          <Heading size={4}>
            How do you want to engage with your audience?
          </Heading>
          <div className={styles.welcomerOptions}>
            <RadioInput
              checked={!iceBreaker.active}
              css={{
                input: styles.welcomerTypeOption,
                labelTitle: styles.welcomerTypeOptionLabel,
              }}
              name="iceBreaker"
              value="false"
              onChange={() => activateIceBreaker(false)}
              label={
                <>
                  <span>Show Get Started Button</span>
                  <Tooltip
                    title="If you choose this option, users will see a Get Started
                    button. When they click on this button the following chat
                    dialogue will be triggered."
                  >
                    <QuestionCircleIcon
                      color="#262626"
                      style={{ width: '14px', height: '14px' }}
                    />
                  </Tooltip>
                </>
              }
            />
            <RadioInput
              checked={iceBreaker.active}
              css={{
                input: styles.welcomerTypeOption,
                labelTitle: styles.welcomerTypeOptionLabel,
              }}
              name="iceBreaker"
              value="true"
              onChange={() => activateIceBreaker(true)}
              label={
                <>
                  <span>Show Icebreakers</span>
                  <Tooltip
                    title="Icebreakers are pre-populated Frequently Asked Questions
                    that customers can click on to start a conversation with
                    your Facebook page. Use this option if your customers
                    frequently ask the same questions."
                  >
                    <QuestionCircleIcon
                      color="#262626"
                      style={{ width: '14px', height: '14px' }}
                    />
                  </Tooltip>
                </>
              }
            />
          </div>
          {Boolean(iceBreaker?.active) && (
            <div className={styles.iceBreakerInfo}>
              <div>
                Add some frequently asked questions to help customers start a
                conversation with your Instagram account.
              </div>
              <InfoCircleOutlined />
            </div>
          )}
        </div>
        {iceBreaker.active ? (
          <div className={styles.iceBreakerContent}>
            <div className={styles.iceBreakerEditContainer}>
              <MMdndContext.Provider value={dndEvents}>
                <DragDropContext
                  onDragStart={dndEvents.onDragStart}
                  onDragUpdate={dndEvents.onDragUpdate}
                  onDragEnd={dndEvents.onDragEnd}
                >
                  <IceBreaker
                    iceBreaker={iceBreaker}
                    platform="facebook"
                    showHeading={false}
                  />
                </DragDropContext>
              </MMdndContext.Provider>
            </div>
            {!isMobile && (
              <div className={styles.iceBreakerPreviewContainer}>
                <IceBreakerPreview iceBreaker={iceBreaker} />
              </div>
            )}
          </div>
        ) : (
          <EmbeddableDialogueEditorWithPreview
            className={styles.dialogueEditor}
            dialogueEditorClassName={styles.dialogueContainer}
            dialogueId={welcomePage.id}
            platforms={['facebook']}
            usedInSlideout
            hideDialogueSelector
            withPreview
          />
        )}
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
